import { FC, useEffect, useState } from 'react';
import { GridColDef, GridSortModel } from '@mui/x-data-grid';
import StyledGridContainer, {
  CenteredCellBox,
} from 'src/components/StyledComponents/StyledGridContainer';
import Status from 'src/components/Status';
import CustomNoRowsOverlay from 'src/components/StyledComponents/CustomEmptyTableRow';
import { GRNPayment } from 'src/models/Management.interface';
import LoadingTable from 'src/components/StyledComponents/CustomLoadingTable';
import StyledDataGrid from 'src/components/StyledComponents/StyledDataGrid';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import PaginationControl from 'src/components/StyledComponents/StyledPagination';
import { useGetGRNPayments } from 'src/services/grnPayment.service';
import GRNPaymentActionMenu from 'src/components/ActionButtons/GRNPaymentActionMenu';
import { loadData } from 'src/utils/grid.utils';
import { pagination } from 'src/utils/properties.utils';
import { formatCurrency } from 'src/utils/format.utils';
import {
  getPaymentStatusText,
  getPaymentModeText,
} from 'src/utils/status.utils';
import { PaymentMode } from 'src/models/enums/PaymentMode.enum';
const GRNPaymentGrid: FC<{
  grnId: string;
  state: boolean;
  filters: {
    status: string;
    startDate: Date | null;
    endDate: Date | null;
  };
  resetPageSignal?: number;
}> = ({ grnId, state, filters, resetPageSignal }) => {
  const { showSnackbar } = StyledSnackbar();
  const [page, setPage] = useState<number>(pagination.DEFAULT_PAGE);
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(
    pagination.DEFAULT_PAGE_SIZE
  );
  const [sortBy, setSortBy] = useState<string>('paymentDate');
  const [order, setOrder] = useState<'ASC' | 'DESC'>('DESC');
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'paymentDate', sort: 'desc' },
  ]);
  const { loading, payments, totalItems, fetchPayments, deletePayment } =
    useGetGRNPayments();

  useEffect(() => {
    if (grnId) {
      loadData(
        () =>
          fetchPayments(
            grnId,
            page,
            pageSize,
            sortBy,
            order,
            filters.status,
            filters.startDate,
            filters.endDate
          ),
        showSnackbar
      );
    }
  }, [state, grnId, page, pageSize, filters, sortBy, order, refreshTrigger]);

  useEffect(() => {
    if (resetPageSignal !== undefined && resetPageSignal > 0) {
      setPage(1);
    }
  }, [resetPageSignal]);

  const handleSortModelChange = (newSortModel: GridSortModel) => {
    setSortModel(newSortModel);
    const order = (newSortModel[0]?.sort || 'asc').toUpperCase();
    setPage(1);
    const sortBy =
      newSortModel[0]?.field === 'amount'
        ? 'paidAmount'
        : newSortModel[0]?.field || 'paymentDate';
    setSortBy(sortBy);
    setOrder(order as 'ASC' | 'DESC');
  };

  const columns: GridColDef[] = [
    {
      field: 'rowNumber',
      headerName: '#',
      align: 'center',
      flex: 0.2,
      sortable: false,
      headerAlign: 'center',
    },
    {
      field: 'paymentDate',
      headerName: 'Payment Date',
      flex: 0.6,
    },
    {
      field: 'paymentMode',
      headerName: 'Payment Mode',
      flex: 1,
    },
    {
      field: 'amount',
      headerName: 'Amount',
      flex: 0.6,
      align: 'right',
      headerAlign: 'right',
      sortable: false,
      renderCell: (params) => formatCurrency(params.value),
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.8,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <CenteredCellBox>
          <Status status={params.value} />
        </CenteredCellBox>
      ),
    },

    {
      field: 'action',
      headerName: 'Action',
      flex: 0.3,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <GRNPaymentActionMenu
          rowId={params.id}
          id={params.row.id}
          grnId={grnId}
          status={params.row.rawStatus}
          paymentMode={params.row.rawPaymentMode}
          resetPageAndRefresh={() => {
            if (page === 1) {
              setRefreshTrigger((prev) => prev + 1);
            } else {
              setPage(1);
            }
          }}
          triggerRefresh={() => {
            setRefreshTrigger((prev) => prev + 1);
          }}
          deletePayment={(paymentId: string) => deletePayment(paymentId, grnId)}
        />
      ),
    },
  ];

  const transformData = (payments: GRNPayment[]) => {
    if (!payments || payments.length === 0) {
      return [];
    }

    return payments.map((payment, index) => {
      let paymentModeDisplay = getPaymentModeText(payment.paymentMode);

      if (payment.paymentMode === PaymentMode.CHEQUE && payment.chequeNumber) {
        const chequeDetails = payment.chequeNumber;
        if (payment.chequeDate) {
          const formattedChequeDate = new Date(
            payment.chequeDate
          ).toLocaleDateString('en-GB');
          paymentModeDisplay = `Cheque - ${chequeDetails} (${formattedChequeDate})`;
        } else {
          paymentModeDisplay = `Cheque - ${chequeDetails}`;
        }
      }

      if (payment.paymentMode === PaymentMode.ONLINE) {
        const bankName = payment.bankName || '';
        const accountNumber = payment.accountNumber || '';
        if (bankName && accountNumber) {
          paymentModeDisplay = `Online - ${bankName} - ${accountNumber}`;
        } else if (bankName) {
          paymentModeDisplay = `Online - ${bankName}`;
        } else if (accountNumber) {
          paymentModeDisplay = `Online - ${accountNumber}`;
        }
      }

      return {
        id: payment.id,
        rowNumber: (page - 1) * pageSize + index + 1,
        paymentDate: new Date(payment.paymentDate).toLocaleDateString('en-GB'),
        amount: payment.amount,
        status: getPaymentStatusText(payment.status),
        rawStatus: payment.status,
        paymentMode: paymentModeDisplay,
        rawPaymentMode: payment.paymentMode,
      };
    });
  };

  const rows = transformData(payments);

  return loading ? (
    <LoadingTable columns={columns} />
  ) : (
    <StyledGridContainer
      pagination={
        totalItems > 0 ? (
          <PaginationControl
            totalItems={totalItems}
            page={page}
            setPage={setPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
            loading={loading}
          />
        ) : undefined
      }
    >
      <StyledDataGrid
        rows={rows}
        columns={columns}
        disableColumnMenu
        sortModel={sortModel}
        slots={{
          noRowsOverlay: CustomNoRowsOverlay,
        }}
        onSortModelChange={handleSortModelChange}
        sortingMode="server"
        sortingOrder={['asc', 'desc']}
      />
    </StyledGridContainer>
  );
};

export default GRNPaymentGrid;
